import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Pagination,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import ConfigManagementService, { ConfigScheme } from '../../../../services/ConfigManagementService';
import { BaseButton, BaseTextField } from '../../base';
import PageHeader from '../../base/PageHeader';
import { CollectionTable } from '../config/components/CollectionTable';
import { CollectionItemModal, EditingCollectionItem } from '../config/components';
import { ConfigurationTab } from '../config/components/ConfigurationTab';
import { useCollectionFilters, useLookupCache } from '../../../../hooks';
import { getEditableCollectionProperties } from '../../../../utils/collectionHelpers';

/**
 * Scheme Manager Page
 * Dedicated page for managing a single pinned schema's data.
 * Route: /admin/manager/:schemeKey
 */
const SchemeManagerPage: React.FC = () => {
  const { schemeKey } = useParams<{ schemeKey: string }>();
  const navigate = useNavigate();

  // ── State ──
  const [scheme, setScheme] = useState<ConfigScheme | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  // Collection state
  const [collectionItems, setCollectionItems] = useState<any[]>([]);
  const [collectionSchema, setCollectionSchema] = useState<Record<string, any>>({});
  const collectionFilters = useCollectionFilters('createdAt', 'desc');
  const { cache: lookupCache, setCachedData } = useLookupCache();

  // Inline row editing
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<Record<string, any>>({});

  // Modal editing
  const [collectionItemModalOpen, setCollectionItemModalOpen] = useState(false);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [editingItem, setEditingItem] = useState<EditingCollectionItem | null>(null);

  // Config state
  const [configData, setConfigData] = useState<Record<string, any> | null>(null);

  // ── Data loading ──
  const loadCollectionItems = async (
    key: string,
    page: number = 1,
    search: string = '',
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
  ) => {
    try {
      setLoading(true);
      const schemeData = await ConfigManagementService.getSchemeByKey(key);
      const schemaDefinition = schemeData.schemaDefinition?.items || schemeData.schemaDefinition || {};
      setCollectionSchema(schemaDefinition);

      const searchFields = ConfigManagementService.getAvailableSearchFields(schemeData.schemaDefinition);
      const response = await ConfigManagementService.searchCollectionItems(
        key,
        search,
        page,
        10,
        sortBy ?? collectionFilters.sortBy,
        sortOrder ?? collectionFilters.sortOrder,
        searchFields,
      );

      setCollectionItems(response.items);
      collectionFilters.setPaging(
        response.pagination ?? {
          page,
          limit: 10,
          total: response.items?.length ?? 0,
          pages: Math.ceil((response.items?.length ?? 0) / 10),
        },
      );
      collectionFilters.setPage(page);

      // Load lookup data
      const schemaProperties = schemaDefinition.properties || schemaDefinition;
      const lookupFields = Object.values(schemaProperties)
        .filter((fs: any) => fs['x-control']?.type === 'lookup' && fs['x-control']?.settings?.source)
        .map((fs: any) => fs['x-control'].settings);
      for (const cfg of lookupFields) {
        try {
          const res = await ConfigManagementService.searchCollectionItems(
            cfg.source, '', 1, 100, 'createdAt', 'desc', [cfg.renderProp],
          );
          res.items?.forEach((item: any) => setCachedData(cfg.source, item.id, item.data));
        } catch {
          // ignore lookup errors
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigData = async (key: string) => {
    try {
      const data = await ConfigManagementService.getConfig(key);
      setConfigData(data?.config ?? {});
    } catch {
      setConfigData({});
    }
  };

  const loadScheme = async () => {
    if (!schemeKey) return;
    try {
      setLoading(true);
      setError(null);
      const data = await ConfigManagementService.getSchemeByKey(schemeKey);
      setScheme(data);

      if (data.type === 'collection') {
        await loadCollectionItems(schemeKey, 1, '');
      } else {
        await loadConfigData(schemeKey);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schema');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeKey]);

  // ── Collection handlers ──
  const handleSearch = async (search: string) => {
    collectionFilters.setSearch(search);
    if (schemeKey) await loadCollectionItems(schemeKey, 1, search);
  };

  const handlePageChange = async (page: number) => {
    if (schemeKey) await loadCollectionItems(schemeKey, page, collectionFilters.search);
  };

  const handleSort = async (column: string) => {
    const newOrder =
      column === collectionFilters.sortBy && collectionFilters.sortOrder === 'asc' ? 'desc' : 'asc';
    collectionFilters.toggleSort(column);
    if (schemeKey) await loadCollectionItems(schemeKey, 1, collectionFilters.search, column, newOrder);
  };

  const handleOpenCreate = () => {
    if (!schemeKey) return;
    const emptyData: Record<string, any> = {};
    Object.keys(getEditableCollectionProperties(collectionSchema)).forEach((k) => { emptyData[k] = ''; });
    setIsCreatingItem(true);
    setEditingItem({ schemaKey: schemeKey, data: emptyData, schema: collectionSchema });
    setCollectionItemModalOpen(true);
  };

  const handleEditItem = (id: string) => {
    if (!schemeKey) return;
    const item = collectionItems.find((i) => i.id === id);
    if (!item) return;
    setIsCreatingItem(false);
    setEditingItem({ schemaKey: schemeKey, id, data: { ...item.data }, schema: collectionSchema });
    setCollectionItemModalOpen(true);
  };

  const handleSaveItem = async (formData: Record<string, any>) => {
    if (!editingItem || !schemeKey) return;
    try {
      setSaving(true);
      setError(null);
      if (isCreatingItem) {
        await ConfigManagementService.createCollectionItem(schemeKey, formData);
        setSuccess('Item created successfully');
      } else {
        await ConfigManagementService.updateCollectionItem(editingItem.id!, schemeKey, formData);
        setSuccess('Item updated successfully');
      }
      setCollectionItemModalOpen(false);
      setEditingItem(null);
      await loadCollectionItems(schemeKey, collectionFilters.page, collectionFilters.search);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!schemeKey || !window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await ConfigManagementService.deleteCollectionItem(id, schemeKey);
      setSuccess('Item deleted successfully');
      await loadCollectionItems(schemeKey, collectionFilters.page, collectionFilters.search);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    }
  };

  // Inline row edit handlers
  const handleStartEditRow = (item: any) => {
    setEditingRowId(item.id);
    setEditingRowData({ ...item.data });
  };
  const handleCancelEditRow = () => { setEditingRowId(null); setEditingRowData({}); };
  const handleSaveEditRow = async () => {
    if (!editingRowId || !schemeKey) return;
    try {
      setSaving(true);
      await ConfigManagementService.updateCollectionItem(editingRowId, schemeKey, editingRowData);
      setEditingRowId(null);
      setEditingRowData({});
      await loadCollectionItems(schemeKey, collectionFilters.page, collectionFilters.search);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save row');
    } finally {
      setSaving(false);
    }
  };
  const handleEditCellChange = (field: string, value: any) => {
    setEditingRowData((prev) => ({ ...prev, [field]: value }));
  };

  // ── Config handler ──
  const handleSaveConfig = async (data: Record<string, any>) => {
    if (!schemeKey) return;
    try {
      setSaving(true);
      await ConfigManagementService.updateConfig(schemeKey, data);
      setSuccess('Configuration saved successfully');
      setConfigData(data);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──
  if (loading && !scheme) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!scheme && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Schema "{schemeKey}" not found.</Alert>
        <BaseButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/configs')}
          sx={{ mt: 2 }}
        >
          Back to Config Management
        </BaseButton>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title={scheme?.label || scheme?.key || schemeKey || ''}
        subtitle={
          scheme?.description ||
          `Manage ${scheme?.type === 'collection' ? 'collection items' : 'configuration'} for this schema`
        }
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Manager' },
          { label: scheme?.label || scheme?.key || schemeKey || '' },
        ]}
        actions={[
          ...(scheme?.type === 'collection' ? [{
            label: 'New Item',
            onClick: handleOpenCreate,
            icon: <AddIcon />,
            variant: 'contained' as const,
            colorVariant: 'primary' as const,
          }] : []),
          {
            label: 'Back',
            onClick: () => navigate('/admin/configs'),
            icon: <ArrowBackIcon />,
            variant: 'outlined' as const,
            colorVariant: 'secondary' as const,
          },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #E6E7EB' }}>
        {scheme?.type === 'collection' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #E6E7EB',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                bgcolor: '#fafafa',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <BaseTextField
                  placeholder="Search items..."
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); handleSearch(e.target.value); }}
                  size="small"
                  startIcon={<SearchIcon />}
                  endIcon={
                    searchInput ? (
                      <Box
                        component="span"
                        onClick={() => { setSearchInput(''); handleSearch(''); }}
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#999' }}
                      >
                        <ClearIcon fontSize="small" />
                      </Box>
                    ) : null
                  }
                />
              </Box>
              <IconButton
                onClick={() => schemeKey && loadCollectionItems(schemeKey, collectionFilters.page, collectionFilters.search)}
                disabled={loading}
                size="small"
                sx={{ border: '1px solid #E6E7EB', borderRadius: '6px' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Table */}
            <Box sx={{ p: 2, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : collectionItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="textSecondary" sx={{ mb: 2 }}>
                    No items in this collection yet.
                  </Typography>
                  <BaseButton variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                    Create First Item
                  </BaseButton>
                </Box>
              ) : (
                <CollectionTable
                  items={collectionItems}
                  schemaKey={schemeKey ?? null}
                  schema={collectionSchema}
                  editingRowId={editingRowId}
                  editingRowData={editingRowData}
                  sortBy={collectionFilters.sortBy}
                  sortOrder={collectionFilters.sortOrder}
                  lookupCache={lookupCache}
                  saving={saving}
                  onStartEdit={handleStartEditRow}
                  onCancelEdit={handleCancelEditRow}
                  onSaveEdit={handleSaveEditRow}
                  onEditCellChange={handleEditCellChange}
                  onSort={handleSort}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                />
              )}
            </Box>

            {/* Pagination */}
            {collectionItems.length > 0 && collectionFilters.paging.pages > 1 && (
              <Box
                sx={{
                  p: 2,
                  borderTop: '1px solid #E6E7EB',
                  bgcolor: '#f5f5f5',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Pagination
                  count={collectionFilters.paging.pages}
                  page={collectionFilters.page}
                  onChange={(_, page) => handlePageChange(page)}
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Box>
        ) : (
          <ConfigurationTab
            schemes={scheme ? [scheme] : []}
            selectedConfigKey={schemeKey ?? null}
            configData={configData}
            loading={loading}
            saving={saving}
            onSelectConfig={() => {}}
            onSaveConfig={handleSaveConfig}
            onRefresh={() => schemeKey && loadConfigData(schemeKey)}
          />
        )}
      </Paper>

      {editingItem && (
        <CollectionItemModal
          open={collectionItemModalOpen}
          isCreating={isCreatingItem}
          editingItem={editingItem}
          saving={saving}
          onClose={() => {
            setCollectionItemModalOpen(false);
            setIsCreatingItem(false);
          }}
          onSave={handleSaveItem}
        />
      )}
    </Box>
  );
};

export default SchemeManagerPage;
